<template>
  <ul>
    <li
      v-for="otherCheckbox in otherCheckboxes"
      :key="otherCheckbox.category"
      class="list-items"
    >
      <ApplicationListCheck
        :hide-icon="true"
        :is-disabled="isIncoming || isRemoved"
        :is-checked="checkedFiles.includes(otherCheckbox.category)"
        :icon-url="getCheckboxIconUrl(otherCheckbox.category)"
        :comment="null"
        :has-files="false"
        :icon-title="getCheckboxTitle(otherCheckbox.category)"
        @checked="
          $emit('checksChange', {
            checked: $event,
            category: otherCheckbox.category
          })
        "
        @selectedComment="$emit('selectedComment', $event)"
        >{{ getLabel(otherCheckbox.category) }}: {{ otherCheckbox.value }}
      </ApplicationListCheck>
    </li>
    <li
      v-for="category in allDocumentsUploadedForType"
      :key="category"
      class="list-items"
    >
      <ApplicationListCheck
        :hide-icon="!isIncoming"
        :is-disabled="isIncoming || isRemoved"
        :is-checked="checkedFiles.includes(getCategoryId(category))"
        :icon-url="getCheckboxIconUrl(category)"
        :comment="getComment(category)"
        :has-files="!isIncoming && fileExists(category)"
        :icon-title="getCheckboxTitle(category)"
        @checked="
          $emit('checksChange', {
            checked: $event,
            category: getCategoryId(category)
          })
        "
        @selectedComment="
          $emit('selectedComment', {
            isCurrentTenant: isCurrentTenant,
            documentType: $event.documentType,
            content: $event.content
          })
        "
        @openFile="
          $emit('openFile', {
            index: 0,
            category,
            categoryTypes: singleDocsMode
              ? [null, 'current_tenant']
              : activeCategoryTypes
          })
        "
        >{{ getLabel(category) }}
      </ApplicationListCheck>
    </li>
  </ul>
</template>

<script>
import svgs from '@/utils/icon-constants';
import docTypeUtils from '@/utils/docs-utils';
import ApplicationListCheck from '@/components/ApplicationListCheck.vue';

export default {
  components: {
    ApplicationListCheck
  },
  props: {
    application: {
      type: Object,
      required: true
    },
    files: {
      type: Array,
      required: true
    },
    checkedFiles: {
      type: Array,
      required: true
    },
    otherCheckboxes: {
      type: Array,
      default: () => []
    },
    applicationStatus: {
      type: String,
      default: null
    },
    singleDocsMode: {
      type: Boolean,
      required: true
    },
    isCurrentTenant: {
      type: Boolean,
      default: true
    }
  },
  emits: ['checksChange', 'openFile', 'selectedComment'],

  data() {
    return {
      svgs
    };
  },
  computed: {
    adminNotes() {
      return this.application.adminNotes;
    },
    allDocumentsUploadedForType() {
      return [
        ...this.requiredDocumentsForType,
        ...this.noneRequiredDocumentsForType
      ];
    },
    requiredDocumentsForType(state) {
      if (this.isCurrentTenant) {
        if (!state.application?.computedDocumentsGroups) {
          return [];
        }
        return Object.entries(state.application.computedDocumentsGroups)
          .filter(([, props]) => props.required === 1)
          .map(item => item[0]);
      }
      if (!state.application?.inviteeComputedDocumentsGroups) {
        return [];
      }
      return Object.entries(state.application.inviteeComputedDocumentsGroups)
        .filter(([, props]) => props.required === 1)
        .map(item => item[0]);
    },
    noneRequiredDocumentsForType(state) {
      if (this.isCurrentTenant) {
        if (!state.application?.computedDocumentsGroups) {
          return [];
        }
        return Object.entries(state.application.computedDocumentsGroups)
          .filter(([, props]) => props.required === 0)
          .map(item => item[0]);
      }
      if (!state.application?.inviteeComputedDocumentsGroups) {
        return [];
      }
      return Object.entries(state.application.inviteeComputedDocumentsGroups)
        .filter(([, props]) => props.required === 0)
        .map(item => item[0]);
    },
    isIncoming() {
      return this.applicationStatus === 'INCOMING';
    },
    isRemoved() {
      return this.applicationStatus === 'REMOVED';
    },
    activeFiles() {
      if (!this.singleDocsMode) {
        return this.files.filter(file =>
          this.activeCategoryTypes.includes(file.categoryType)
        );
      }
      return this.files;
    },
    activeCategoryTypes() {
      if (!this.isCurrentTenant) {
        return ['secondary_tenant'];
      }
      return ['current_tenant', null];
    }
  },
  methods: {
    getComment(category) {
      if (
        !this.activeCategoryTypes.some(
          activeCategoryType => !!this.adminNotes[activeCategoryType]
        )
      ) {
        return null;
      }

      const adminNotes = this.activeCategoryTypes
        .map(activeCategoryType => this.adminNotes[activeCategoryType])
        .flat();

      const note = adminNotes
        .filter(n => !!n)
        .find(note => note.category === category);

      if (!note) {
        return null;
      }
      return {
        content: note.note,
        documentType: category
      };
    },
    getCategoryId(category) {
      if (!this.singleDocsMode) {
        return `${category}-${this.activeCategoryTypes[0]}`;
      }
      return category;
    },
    fileExists(category) {
      return this.activeFiles.some(file => file.type === category);
    },
    getCheckboxIconUrl(category) {
      const fileExist = this.fileExists(category);
      if (fileExist) {
        return this.svgs.ICONS.CHECK;
      }
      return this.svgs.ICONS.YELLOW_CLOCK;
    },
    getCheckboxTitle(category) {
      if (this.getComment(category)) {
        return 'Det finns en kommentar angående dessa uppgifter';
      }

      const fileExist = this.fileExists(category);
      if (fileExist) {
        return 'Uppgifter inskickade';
      }

      return 'Uppgifter har inte skickats in';
    },
    getLabel(category) {
      return docTypeUtils.getDocTypeStr(category);
    }
  }
};
</script>

<style scoped>
.list-items {
  display: flex;
  font-weight: 400;
  font-size: 12px;
  padding: 2px 0 2px 0;
}
.list-items .text-icon {
  width: 100%;
}
.list-items :deep(.icon-wrapper) {
  flex-shrink: 0;
}

.divider {
  width: 100%;
  height: 1px;
  background-color: var(--color-light-grey);
  border: none;
  margin: 5px 0;
}
</style>
